import { DocusealForm } from "@docuseal/react";
import AngleLeft from "@icons/angle-left.svg?react";
import Download from "@icons/download.svg?url";
import { Trans } from "@lingui/react/macro";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import invariant from "tiny-invariant";

import { CanISignContractGuard } from "@/common/acl/statutory-guard/contract-sign-guard";
import { Button, secondary } from "@/common/components/button/button";
import { Info } from "@/common/components/info/info";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spinner } from "@/common/components/spinner/spinner";
import { useLanguage } from "@/common/hooks/use-language";
import { useToggle } from "@/common/hooks/use-toggle";
import { useCompany } from "@/common/providers/company-provider";
import { ContractStatus } from "@/generated/digitalnisklady.cz/graphql";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { Failed } from "../shared/routes/failed/failed";
import {
  confirmationQuery,
  ConfirmationQueryResult,
  contractQuery,
  ContractQueryResult,
} from "./contracts.api";

const path = "signature";

const Signature = () => {
  const { contractId } = useParams();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(contractId, "contractId param is not defined");
  const locale = useLanguage();
  const contract = useSuspenseQuery(contractQuery({ id: contractId, locale }));
  const confirmation = useSuspenseQuery(confirmationQuery({ contractId }));

  return (
    <CanISignContractGuard>
      <CanContractBeSigned status={contract.data.status}>
        <FullscreenModalLayout
          headerButton={
            <SearchParamsLink to="..">
              <AngleLeft />
            </SearchParamsLink>
          }
        >
          <FullscreenModalLayout.Title>
            <Trans>Podepsat smlouvu</Trans>
          </FullscreenModalLayout.Title>
          <ContractStatusComponent
            confirmation={confirmation.data}
            contract={contract.data}
          />
        </FullscreenModalLayout>
      </CanContractBeSigned>
    </CanISignContractGuard>
  );
};

const ContractStatusComponent = ({
  contract,
  confirmation,
}: {
  contract: ContractQueryResult;
  confirmation: ConfirmationQueryResult;
}) => {
  if (
    contract.status === ContractStatus.Confirmed &&
    !confirmation?.signedByTrader
  ) {
    return (
      <Info>
        <Trans>
          Smlouva byla schválena a čeká na podpis obchodníka. Zkuste akci
          opakovat za chvíli.
        </Trans>
      </Info>
    );
  }

  if (!confirmation || !confirmation.signedByTrader) {
    return (
      <Info>
        <Trans>
          Smlouvu nyní nelze podepsat, čeká na schválení protistrany.
        </Trans>
      </Info>
    );
  }

  if (confirmation.confirmation?.completed) {
    return (
      <>
        <Info>
          <Trans>Smlouva již byla podepsána</Trans>
        </Info>
        <Button.Container>
          <a
            className={secondary}
            href={confirmation.confirmation.documents.at(0)?.url}
            rel="noreferrer"
            style={{
              background: `url(${Download}) left 16px center no-repeat`,
            }}
            target="_blank"
          >
            <Trans>Stáhnout smlouvu v PDF</Trans>
          </a>
        </Button.Container>
      </>
    );
  }

  if (confirmation.docusealSlug) {
    return <Form slug={confirmation.docusealSlug} />;
  }
};

const Form = ({ slug }: { slug: string }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const callbackUrl =
    location.pathname.replace("signature", "success") + "?" + location.search;
  const { user } = useCompany();
  const formReady = useToggle();

  const handleComplete = async () => {
    void queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes("confirmation"),
    });
    await navigate(callbackUrl);
  };

  return (
    <>
      {formReady.on ? null : <Spinner className="mt-2" withHint={false} />}
      <DocusealForm
        allowToResubmit={false}
        backgroundColor="#FFFFFF"
        email={user.email}
        language={user.language}
        onComplete={handleComplete}
        onLoad={formReady.toggle}
        src={`https://docuseal.eu/s/${slug}`}
        withDownloadButton={false}
        withSendCopyButton={false}
        withTitle={false}
        rememberSignature
        // eslint-disable-next-line lingui/no-unlocalized-strings
        customCss={`
          * {
            font-family: Roboto
          }

          div.field-area {
            font-size: 6pt !important;
          }

          #submit_form_button {
            border: none;
            border-radius: 16px;
            color: #FFFFFF;
            font-weight: bold;
            background: var(--gradient, linear-gradient(80deg, #60A6D3 0%, #58B4A1 97.67%));
            box-shadow: 2px 4px 12px 0px rgba(122, 128, 147, 0.25);
          }

          input.base-input {
            border-radius: 8px;
            color: #616161;
            font-size: 1rem !important;
          }

          input.base-input:focus {
            outline-style: none
          }

          button#minimize_form_button {
            display: none
          }

          form#form_container {
            padding-top: 0;
          }

          div.tooltip {
            display: none;
          }

          div#form_container label.label {
            color: #616161;
            font-size: 1rem;
            margin: 0;
          }

          a.btn-ghost {
            color: #616161
          }

          a.btn-ghost:hover {
            background-color: initial;
          }

          a#type_text_button,
          a.btn-outline,
          label.btn-outline {
            border-radius: 8px;
            color: #168B6B;
          }

          a#type_text_button:hover,
          a.btn-outline:hover,
          label.btn-outline:hover {
            background-color: #FFFFFF;
            border-color: #168B6B;
            color: #168B6B;
          }

          .tooltip:after,
          .tooltip:before {
            display: none
          }

          div.space-x-2:has(> span:nth-of-type(2)) {
            overflow-x: auto
          }
        `}
      />
    </>
  );
};

const CanContractBeSigned = ({
  children,
  status,
}: React.PropsWithChildren<{ status: ContractStatus }>) => {
  switch (status) {
    case ContractStatus.Pending:
    case ContractStatus.Processing:
      return (
        <Failed
          content={
            <Trans>
              Smlouvu nyní nelze podepsat, čeká na schválení a podpis
              obchodníka.
            </Trans>
          }
          title=""
        />
      );
    case ContractStatus.Signed:
    case ContractStatus.Confirmed:
    case ContractStatus.Success:
    case ContractStatus.Failed:
      return <>{children}</>;
    default: {
      const exhaustiveCheck: never = status;
      throw new Error(`Unhandled status case: ${exhaustiveCheck}`);
    }
  }
};

export { path, Signature };
